// File: src/components/UnsavedChangesBottomSheet.jsx
import React, { useEffect, useRef } from 'react';
import { FaExclamationTriangle } from 'react-icons/fa';

const isPWA = () =>
  (window.matchMedia?.('(display-mode: standalone)').matches) ||
  window.navigator.standalone === true;

export default function UnsavedChangesBottomSheet({ onCancel, onDiscard }) {
  const scrollYRef = useRef(0);

  useEffect(() => {
    const meta = document.querySelector('meta[name="theme-color"]');

    // Freeze (match ProfileActionSheet/TourBottomSheet)
    scrollYRef.current = window.scrollY || window.pageYOffset;
    document.body.style.top = `-${scrollYRef.current}px`;
    document.body.classList.add('overlay-active');
    document.documentElement.classList.add('overlay-active');
    document.getElementById('root')?.classList.add('overlay-active');

    // Notch/status bar color (same design)
    if (meta) meta.content = '#999998';

    return () => {
      // Unfreeze & restore
      const y = scrollYRef.current;
      document.body.classList.remove('overlay-active');
      document.documentElement.classList.remove('overlay-active');
      document.getElementById('root')?.classList.remove('overlay-active');
      document.body.style.top = '';
      window.scrollTo(0, y);

      if (meta) meta.content = '#ffffff';
    };
  }, []);

  return (
    <>
      <div style={S.backdrop} onClick={onCancel}>
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Unsaved changes confirmation"
          style={{
            ...S.sheet,
            paddingBottom: `calc(12px + env(safe-area-inset-bottom,0px) + ${isPWA() ? 8 : 0}px)`,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Top bar */}
          <div style={S.topBar}>
            <div style={S.pill}>Heads up</div>
            <button type="button" onClick={onCancel} aria-label="Close" style={S.iconBtn}>
              {/* centered × */}
              <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" style={{ display: 'block' }}>
                <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
          </div>

          {/* Hero */}
          <div style={S.hero}>
            <div style={S.iconWrap}>
              <FaExclamationTriangle size={18} />
            </div>
            <h3 style={S.title}>Unsaved changes</h3>
            <p style={S.desc}>
              You’ve made edits that haven’t been saved. Leave this page and discard them?
            </p>
          </div>

          {/* CTA row */}
          <div style={S.ctaRow}>
            <button type="button" onClick={onCancel} style={S.secondaryBtn}>
              Cancel
            </button>
            <button type="button" onClick={onDiscard} style={S.destructiveBtn}>
              Discard changes
            </button>
          </div>

          <style>{`
            @keyframes slideUp { from { transform: translateY(12px); opacity: 0 } to { transform: translateY(0); opacity: 1 } }
            @media (prefers-reduced-motion: reduce) { * { animation: none !important; transition: none !important } }
            button:focus-visible { outline: 0; box-shadow: 0 0 0 3px rgba(59,130,246,.45) }
          `}</style>
        </div>
      </div>
    </>
  );
}

/* ───────────────────────── Styles (Uber-like tokens) */
const S = {
  backdrop: {
    position: 'fixed', inset: 0,
    background: 'rgba(0,0,0,0.40)', // aligned with other sheets
    zIndex: 9999,
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'center',
    padding: 8,
    fontFamily: "'Inter', system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
  },
  sheet: {
    width: '100%',
    maxWidth: 440,
    background: '#ffffff',
    borderRadius: 12,
    boxShadow: '0 8px 28px rgba(0,0,0,0.25)',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    animation: 'slideUp .22s ease-out',
    padding: 16,
    minHeight: 'clamp(360px, 42vh, 540px)',
  },
  topBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: 40,
  },
  pill: {
    background: '#f3f4f6',
    border: '1px solid #e5e7eb',
    color: '#0b0b0b',
    borderRadius: 999,
    padding: '6px 10px',
    fontSize: 12,
    fontWeight: 800,
  },
  iconBtn: {
    background: 'transparent',
    border: '1px solid #e5e7eb',
    width: 32,
    height: 32,
    display: 'inline-flex',         // centered like other files
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 0,
    padding: 0,
    borderRadius: 8,
    color: '#0b0b0b',
    cursor: 'pointer',
  },
  hero: {
    textAlign: 'center',
    padding: '16px 8px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
  },
  iconWrap: {
    width: 56, height: 56, borderRadius: 999,
    margin: '6px auto 10px',
    display: 'grid', placeItems: 'center',
    background: '#FEF3C7',         // amber-100
    color: '#92400E',               // amber-800
    border: '1px solid #FDE68A',   // amber-300
  },
  title: {
    margin: 0,
    fontSize: 18,
    lineHeight: 1.25,
    fontWeight: 900,
    color: '#0b0b0b',
  },
  desc: {
    margin: '6px auto 0',
    fontSize: 14,
    lineHeight: 1.5,
    color: '#4b5563',
    maxWidth: 360,
  },
  ctaRow: {
    display: 'flex',
    gap: 8,
    marginTop: 'auto',
  },
  secondaryBtn: {
    flex: 1,
    background: '#ffffff',
    color: '#0b0b0b',
    border: '1px solid #e5e7eb',
    borderRadius: 10,
    padding: '12px 14px',
    fontSize: 13,
    fontWeight: 900,
    cursor: 'pointer',
  },
  destructiveBtn: {
    flex: 1,
    background: '#B91C1C',
    color: '#ffffff',
    border: '1px solid #B91C1C',
    borderRadius: 10,
    padding: '12px 14px',
    fontSize: 13,
    fontWeight: 900,
    cursor: 'pointer',
  },
};
